import { useRef, useState, useEffect } from 'react';

type UseCopyOptions = {
  duration?: number; // how long the "copied" state stays
};

export const useCopy = (options?: UseCopyOptions) => {
  const { duration = 2000 } = options || {};

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);

      // Set copied state
      setCopiedKey(key);

      // Clear any previous timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout safely
      timeoutRef.current = setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? null : prev));
      }, duration);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    copiedKey,
    copy,
    isCopied: (key: string) => copiedKey === key,
  };
};